<script setup lang="ts">
  import { date } from '@/helpers/converter'
  import type { NewsList } from '@/store/News'
  import { kebabCase } from 'change-case'
  import { PropType } from 'vue'
  import { useDisplay } from 'vuetify'
  const { mobile } = useDisplay()
  const props = defineProps({
    news: {
      type: Object as PropType<NewsList>,
      required: true,
    },
    type: {
      type: String,
      default: 'v1',
    },
  })
</script>

<template>
  <router-link
    :to="{
      name: 'news-detail',
      params: {
        newsId: props.news.id,
        newsType: props.news.category.toLocaleLowerCase(),
        seo: kebabCase(props.news.name),
      },
    }"
  >
    <div
      v-if="props.type == 'v1' || (props.type == 'v2' && !mobile)"
      class="news-summary"
      :class="news.category.toLocaleLowerCase()"
    >
      <NewsImage :news="news" />
      <div class="news-summary__info-top">
        <div class="news-summary__category">{{ news.category }}</div>
        <div class="news-summary__date">{{ date(news.liveDate) }}</div>
      </div>
      <h3>{{ news?.name }}</h3>
      <div class="news-summary__summary">{{ news.summary }}</div>
      <div class="news-summary__info-bottom">
        <div class="news-summary__date">{{ date(news.liveDate) }}</div>
        <div class="news-summary__readmore">Read More</div>
      </div>
    </div>
    <div v-if="props.type == 'v2' && mobile" class="news-summary-m" :class="news.category.toLocaleLowerCase()">
      <div class="news-summary-m__wrapper">
        <div class="news-summary-m__image">
          <NewsImage :news="news" :type="'v2'" />
        </div>
        <div class="news-summary-m__info-right">
          <div class="news-summary__category">{{ news.category }}</div>
          <div class="news-summary__date">{{ date(news.liveDate) }}</div>
          <h3>{{ news?.name }}</h3>
        </div>
      </div>
    </div>
  </router-link>
</template>

<style lang="scss" scoped>
  $newsCategory: (
    update: var(--news-update),
    events: var(--news-events),
    general: var(--news-general),
    community: var(--news-community),
    maintenance: var(--news-maintenance),
    sale: var(--news-sale),
  );
  .news-summary,
  .news-summary-m {
    max-width: 600px;
    height: 440px;
    text-align: left;
    background-color: var(--content-bg-color);
    border-radius: 5px;
    padding: 20px;
    color: var(--content-text-color);
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: box-shadow 0.3s linear;
    box-shadow: 0px 0px 5px 0 rgba(0, 0, 0, 0.2);
    &:hover {
      box-shadow: 0px 0px 7px 0 rgba(0, 0, 0, 0.4);
      :deep(.news-image__image) {
        transform: scale(102%);
      }
    }
    h3 {
      text-transform: uppercase;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      color: #5a5753;
      font-size: 21px;
      height: auto;
      font-family: 'Glegoo';
      font-weight: 700;
      line-height: 1.2em;
      word-break: break-all;
    }
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 2px;
      bottom: 0;
      left: 0;
    }
    @each $key, $val in $newsCategory {
      &.#{$key} {
        .news-summary__category {
          background: #{$val};
        }
      }
    }
  }

  .news-summary__category {
    margin-bottom: 5px;
    padding: 5px 16px 4px;
    border-radius: 15px;
    font-size: 17px;
    // font-weight: 500;
    font-family: 'nexon_lv2_gothic';
    text-transform: uppercase;
    color: #ffffff;
    display: inline-block;
  }
  .news-summary__summary {
    margin: 8px 0 10px;
    height: 56px;
    font-size: 18px;
    /* font-family: lora, Georgia, Cambria, 'Times New Roman', Times, serif; */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .news-summary__info-top {
    margin: 10px 0 10px;
    .news-summary__date {
      display: none;
    }
  }
  .news-summary__info-bottom {
    display: flex;
    justify-content: space-between;
    font-family: 'nexon_lv2_gothic';
    font-size: 16px;
    text-transform: uppercase;
  }

  .news-summary__readmore {
    text-transform: uppercase;
    text-decoration: underline;
    text-underline-offset: 4px;
    color: var(--hyperlink-text-color);
    font-weight: 500;
  }

  @media #{$maxDesktop} {
    .news-summary {
      height: 500px;
    }
  }

  @media #{$tablet} {
    .news-summary {
      max-width: none;
      height: auto;
      padding-bottom: 30px;
      border-radius: 10px;
      h3 {
        -webkit-line-clamp: 2;
      }
    }

    .news-summary__info-top {
      .news-summary__date {
        margin-left: 10px;
        display: inline-block;
      }
    }
    .news-summary__category {
      font-size: 14px !important;
    }
    .news-summary__date {
      font-size: 16px !important;
    }
    .news-summary__info-bottom,
    .news-summary__summary {
      display: none;
    }
  }
  @media (max-width: 700px) {
    .news-summary {
      h3 {
        font-size: 17px !important;
      }
    }
    .news-summary__category {
      font-size: 14px !important;
    }
    .news-summary__date {
      font-size: 14px !important;
    }
  }

  @media #{$mobile} {
    .news-summary__summary {
      font-size: 16px;
      line-height: 1.5em;
      min-height: 3em;
    }
    .news-summary__category {
      display: inline-block;
      font-size: 13px !important;
      margin-bottom: 12px;
    }
    .news-summary__date {
      display: inline-block;
      font-size: 14px !important;
      margin-left: 10px;
    }
    .news-summary-m {
      width: 100%;
      height: auto;
      display: inline-flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      margin-bottom: 20px;
      h3 {
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        font-size: 18px !important;
      }
    }
    .news-summary-m__image {
      width: 50%;
      margin-right: 15px;
    }
    .news-summary-m__wrapper {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: flex-start;
    }
    /* .news-summary__info-bottom {
    font-size: 0.8em;
    display: inline-block;
  } */
    .news-summary-m__info-right {
      width: 49%;
      display: inline-block;
    }
  }

  @media (max-width: 500px) {
    .news-summary__category {
      font-size: 10px !important;
      max-width: fit-content;
      margin-bottom: 3px;
    }
    .news-summary__date {
      font-size: 13px !important;
      margin-left: 0;
      margin-bottom: 5px;
    }
    .news-summary-m__info-right {
      display: flex;
      flex-direction: column;
    }
    .news-summary {
      h3 {
        font-size: 15px !important;
      }
    }
    .news-summary-m {
      padding: 13px;
      h3 {
        font-size: 13px !important;
      }
    }
  }
</style>
